// Creator route shell. Sits above App and dispatches the non-generation
// surfaces — the hash routes for sign-in / settings / account. The
// generation flow itself stays in App.

import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useAuth } from '@vibeshell/hooks';
import { setWorkspaceOwner, findRecentApp } from './lib/recent-apps';
import { App } from './App';
import { V2Shell } from './v2/V2Shell';
import { LearnV2 } from './v2/LearnV2';
import { WorkspaceV2 } from './v2/WorkspaceV2';
import { AppHubV2 } from './v2/AppHubV2';
import { SupportV2 } from './v2/SupportV2';
import { ConnectorsV2 } from './v2/ConnectorsV2';
import { SettingsV2 } from './v2/SettingsV2';
import { AccountV2 } from './v2/AccountV2';
import { SignIn } from './screens/SignIn';
import { Upgrade } from './screens/Upgrade';
import { Copilot } from './screens/Copilot';
import { AdminDashboard } from './screens/AdminDashboard';
import { ErrorState, type ErrorVariant } from './components/ErrorState';
import { CommandPalette } from './components/CommandPalette';
import { CookieBanner } from './components/CookieBanner';

function currentRoute(): string {
  // Strip any ?query suffix (e.g. #account?upgraded=1) for route matching.
  // The `?? ''` covers a hypothetical empty split result so this function
  // satisfies its `: string` return type without a non-null assertion.
  return (location.hash || '').replace(/^#\/?/, '').split('?')[0] ?? '';
}

// §S680 — the ADDRESSABLE editor route (#apps/:id/edit). Returns the app id
// when the route targets the editor, else null. Must be checked BEFORE the
// plain #apps/:id App-Detail match.
function editRouteAppId(route: string): string | null {
  const m = /^apps\/([^/]+)\/edit$/.exec(route);
  return m ? m[1] ?? null : null;
}

export function CreatorRoot(): JSX.Element | null {
  const { session, loading: authLoading } = useAuth();
  const [route, setRoute] = useState(currentRoute());

  // §isolation — bind the Workspace recents bucket to the signed-in identity as
  // soon as auth resolves. This is what stops a second account (or a signed-out
  // visitor) on a shared browser from seeing the previous creator's apps. The
  // wipe only fires on a real-account→different-identity hand-off; the same
  // person signing in for the first time keeps their just-built apps.
  useEffect(() => {
    if (authLoading) return;
    setWorkspaceOwner(session.signedIn ? session.accountId : 'anon');
  }, [authLoading, session.signedIn, session.accountId]);

  useEffect(() => {
    const onHash = () => setRoute(currentRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Per-route document.title — keeps the browser tab honest as the user
  // moves between surfaces. Lighthouse + share-card scrapers both read
  // title; the previous static title meant every Pomi/Workspace tab read
  // "Pomela — Type. Tap. Install." regardless of context.
  useEffect(() => {
    const base = 'Pomela, Type. Tap. Install.';
    const titles: Record<string, string> = {
      '': base,
      signin: 'Sign in · Pomela',
      settings: 'Settings · Pomela',
      account: 'Account · Pomela',
      upgrade: 'Upgrade · Pomela',
      guide: 'Knowledge base · Pomela',
      apps: 'Workspace · Pomela',
      copilot: 'Pomy · Pomela',
      help: 'Support · Pomela',
      admin: 'Admin · Pomela'
    };
    // §S680 — per-app titles for the editor route and App Detail. The name
    // comes from local recents when available (no fetch for a tab title);
    // otherwise fall back to the base title.
    const editId = editRouteAppId(route);
    if (editId) {
      const name = findRecentApp(editId)?.manifest?.name;
      document.title = name ? `Edit · ${name} · Pomela` : `Edit · ${base}`;
      return;
    }
    if (route.startsWith('apps/')) {
      const name = findRecentApp(route.slice('apps/'.length))?.manifest?.name;
      document.title = name ? `${name} · Pomela` : base;
      return;
    }
    document.title = titles[route] ?? base;
  }, [route]);

  const close = (): void => {
    location.hash = '';
  };

  // §launch — the first-visit onboarding gate ("Build your first app") was
  // removed: new users drop straight onto the real create flow (#'' → App →
  // HomeV2 composer) with no interstitial. Auth / attribution / celebration
  // state is independent and unaffected.
  // Each route renders alongside the global CommandPalette so Cmd+K is
  // always available regardless of which surface is mounted.
  const screen = ((): JSX.Element => {
    // §F02 (platform.signin.preserves_prompt) — ROOT CAUSE lives here: rendering
    // #signin (and #account, the real click-path to it) mounts a different
    // screen, so <App key="root"> below unmounts and the home composer's local
    // state (prompt, mode, attachments) is destroyed. Rather than keep App
    // mounted, the draft is preserved out-of-band in sessionStorage — HomeV2
    // stashes it as the user composes and rehydrates on return, and AuthWall
    // stashes an already-submitted prompt. See lib/signin-draft.ts.
    if (route === 'signin') return <SignIn onClose={close} />;
    if (route === 'settings') return <SettingsV2 />;
    if (route === 'account') return <AccountV2 />;
    if (route === 'upgrade') {
      return <Upgrade onClose={close} onSignIn={() => { location.hash = 'signin'; }} />;
    }
    if (route === 'guide') return <LearnV2 />;
    // §HUB — the searchable Integration Hub catalog.
    if (route === 'connectors') return <ConnectorsV2 />;
    // §S680 — addressable EDITOR (#apps/:id/edit): renders App with the id;
    // App restores the session via openApp so editing survives a reload.
    // Checked before the plain #apps/:id App-Detail match below.
    const editId = editRouteAppId(route);
    // key: leaving the editor for '' must remount App fresh (same component
    // type at the same position would otherwise keep the done-session mounted).
    if (editId) return <App key={`edit:${editId}`} editAppId={editId} />;
    // §12.5 — addressable App Detail (#apps/:id) hosts the App Management Shell.
    if (route.startsWith('apps/')) {
      const id = route.slice('apps/'.length);
      return <AppHubV2 appId={id} />;
    }
    if (route === 'apps') return <WorkspaceV2 />;
    if (route === 'copilot') return <Copilot onClose={close} />;
    if (route === 'help') return <SupportV2 />;
    if (route === 'admin') return <AdminDashboard onClose={close} />;
    // §12.5 — URL-addressable branded error surfaces (#error/<variant>); also the
    // target for support deep-links. The crash boundary + App-Detail not-found are
    // separately wired to real conditions.
    if (route === 'error' || route.startsWith('error/')) {
      const variant = (route.split('/')[1] || 'generic') as ErrorVariant;
      return <ErrorState variant={variant} secondaryLabel="Open Workspace" onSecondary={() => { location.hash = 'apps'; }} />;
    }
    if (route === '') return <App key="root" />;
    // §S680 — an unknown hash used to silently render the home canvas, which
    // told the user a mistyped/stale link was "the app working fine". Render
    // the honest branded 404 instead (its primary action goes home).
    return (
      <ErrorState
        variant="notFound"
        primaryLabel="Go home"
        onPrimary={() => { location.hash = ''; }}
        secondaryLabel="Open Workspace"
        onSecondary={() => { location.hash = 'apps'; }}
      />
    );
  })();
  // §V2 EXACT PORT — the spike shell (rail + drawer + menu-fab + global
  // Pomy) wraps every routed surface; SignIn keeps its own full-screen
  // chrome (the spike has no sign-in screen).
  if (route === 'signin') {
    return (
      <>
        {screen}
        <CommandPalette />
        <CookieBanner />
      </>
    );
  }
  return (
    <>
      <V2Shell route={route}>{screen}</V2Shell>
      <CommandPalette />
      {/* §7 — cookie/consent banner (privacy-default OFF until opt-in). */}
      <CookieBanner />
    </>
  );
}
